import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../../hooks/http.hook';
import CreateBusinessHeader from '../../business/components/CreateBusinessHeader';
import CreateFooter from '../../business/components/CreateFooter';
import { createPromoCodePromotion } from '../handlers/promoCodePromotion';
import DeletePromotionButton from '../components/DeletePromotionButton';
import PromotionProductsPicker, {
  PromotionPickerOpenButton,
} from '../components/PromotionProductsPicker';
import '../../business/Business.css';
import './CreatePromoCodePromotionPage.css';

function CreatePromoCodePromotionPage() {
  const navigate = useNavigate();
  const { promotionId } = useParams();
  const { requestWithMeta } = useHttp();
  const isEdit = !!promotionId;

  const [name, setName] = React.useState('');
  const [code, setCode] = React.useState('');
  const [discount, setDiscount] = React.useState('');
  const [promotionLoading, setPromotionLoading] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);
  const [openPicker, setOpenPicker] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!isEdit) return;

    setPromotionLoading(true);
    setSubmitError(null);

    (async () => {
      try {
        const res = await requestWithMeta(`/operator/promotions/${encodeURIComponent(promotionId)}`, 'GET');
        if (cancelled) return;
        if (!res?.ok) {
          setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось загрузить акцию');
          return;
        }
        const promo = res?.data ?? {};
        setName(promo?.name ?? '');

        const conditions = Array.isArray(promo?.conditions) ? promo.conditions : [];
        const cond = conditions[0] || {};
        const codeRaw = cond?.metaData?.code ?? cond?.meta_data?.code ?? '';
        setCode(codeRaw ?? '');

        const rewards = Array.isArray(promo?.rewards) ? promo.rewards : [];
        const firstDiscount = rewards.find((r) => r?.discount !== null && r?.discount !== undefined)?.discount;
        if (firstDiscount !== undefined && firstDiscount !== null) setDiscount(String(firstDiscount));

        const nextSelected = {};
        rewards.forEach((r) => {
          const pid = r?.productId ?? r?.product_id;
          if (pid == null) return;
          nextSelected[String(pid)] = true;
        });
        setSelectedIds(nextSelected);
      } catch (e) {
        if (!cancelled) setSubmitError(e?.message || 'Не удалось загрузить акцию');
      } finally {
        if (!cancelled) setPromotionLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isEdit, promotionId, requestWithMeta]);

  const handleToggleProduct = (productId) => {
    setSelectedIds((prev) => {
      const key = String(productId);
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }
      return next;
    });
  };

  const selectedProductIds = React.useMemo(
    () =>
      Object.entries(selectedIds)
        .filter(([, v]) => !!v)
        .map(([k]) => (Number.isNaN(Number(k)) ? k : Number(k))),
    [selectedIds]
  );

  const numericDiscount = React.useMemo(() => {
    const normalized = String(discount).replace(',', '.').trim();
    if (!normalized) return NaN;
    const n = Number(normalized);
    if (!Number.isFinite(n)) return NaN;
    return Math.min(100, Math.max(0, n));
  }, [discount]);

  const canSubmit =
    name.trim().length > 0 &&
    code.trim().length > 0 &&
    Number.isFinite(numericDiscount) &&
    numericDiscount > 0 &&
    selectedProductIds.length > 0 &&
    !submitting &&
    !promotionLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = isEdit
        ? await requestWithMeta(`/operator/promotions/${encodeURIComponent(promotionId)}`, 'PUT', {
            name: name.trim(),
            imageUrl: null,
            conditions: [
              { typeId: 3, metaData: { code: code.trim() } }
            ],
            rewards: selectedProductIds.map((id) => ({ productId: id, discount: numericDiscount }))
          })
        : await createPromoCodePromotion(requestWithMeta, {
            name: name.trim(),
            code: code.trim(),
            discount: numericDiscount,
            productIds: selectedProductIds,
          });
      if (res.ok) {
        navigate('/promotions');
        return;
      }
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось создать акцию');
    } catch (e) {
      setSubmitError(e?.message || 'Ошибка при создании акции');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (openPicker) {
    return (
      <PromotionProductsPicker
        title="Товары для промокода"
        subtitle="Выберите товары, на которые действует промокод"
        mode="select"
        selectedIds={selectedIds}
        onToggle={handleToggleProduct}
        onClose={() => setOpenPicker(false)}
      />
    );
  }

  return (
    <div className="create-promo-code-root">
      <CreateBusinessHeader
        title={'Промокод'}
        subtitle="Укажите промокод и скидку для выбранных товаров"
        onBack={handleBack}
      />

      <main className="create-promo-code-content">
        <section className="shop-create-section">
          <label className="shop-create-label" htmlFor="promo-name">
            Название акции <b style={{ color: 'red' }}>*</b>
          </label>
          <input
            id="promo-name"
            className="shop-create-input"
            placeholder="Например, Промокод на кофе"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
          />
        </section>

        <section className="shop-create-section">
          <label className="shop-create-label" htmlFor="promo-code">
            Промокод <b style={{ color: 'red' }}>*</b>
          </label>
          <input
            id="promo-code"
            className="shop-create-input"
            placeholder="Например, COFFEE10"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="off"
          />
        </section>

        <section className="shop-create-section">
          <label className="shop-create-label" htmlFor="promo-discount">
            Скидка по промокоду (%) <b style={{ color: 'red' }}>*</b>
          </label>
          <input
            id="promo-discount"
            className="shop-create-input"
            placeholder="Например, 10"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            autoComplete="off"
            inputMode="decimal"
          />
        </section>

        <section className="shop-create-section">
          <PromotionPickerOpenButton
            title="Товары для промокода"
            hint={
              selectedProductIds.length > 0
                ? `Выбрано: ${selectedProductIds.length}`
                : 'Выбрать товары'
            }
            onClick={() => setOpenPicker(true)}
          />
        </section>

        {submitError ? (
          <div className="create-promo-code-error">{submitError}</div>
        ) : null}
      </main>

      <CreateFooter
        disabled={!canSubmit}
        onCreate={handleSubmit}
        label={
          submitting
            ? isEdit
              ? 'Сохранение…'
              : 'Создание…'
            : isEdit
              ? 'Сохранить'
              : 'Создать акцию'
        }
        leading={
          isEdit ? (
            <DeletePromotionButton
              promotionId={promotionId}
              promotionName={name}
              requestWithMeta={requestWithMeta}
              disabled={submitting}
              onDeleted={() => navigate('/promotions')}
            />
          ) : null
        }
      />
    </div>
  );
}

export default CreatePromoCodePromotionPage;

